import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createProcurementWorkflowSteps,
  getProcurementWorkflowSteps,
  updateProcurementWorkflowSteps,
} from 'api/procurement.api'
import { CloseIcon } from 'assets/icons/CloseIcon'
import { PlusIcon } from 'assets/icons/PlusIcon'
import { ReorganizeIcon } from 'assets/icons/ReorganizeIcon'
import { Alert, AlertType } from 'components/Alert/Alert'
import { Card, CardBody, StyckyFooter } from 'components/Card/Card'
import { JustSpinner, Spinner } from 'components/Loading/Loading'
import { DepartmentSelect } from 'components/Select/Department.select'
import { EmptyList } from 'components/Table/EmptyList'
import { useFilter } from 'hooks/useFilter'
import { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { Button, ColorVariant, Flex, Icon } from 'styling/GlobalStyles'
import { MediumText, NormalText } from 'styling/TypographyV2'
import { WorkflowResolverType, WorkflowStepType } from 'types/procurement'
import { DndContext, DragEndEvent, DragOverlay } from '@dnd-kit/core'
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

import {
  CurrentWorkflowStep,
  LocalWorkflowOptionStep,
  ProcurementApprovalPolicyWorkflowStepRoleResponse,
  ProcurementApprovalPolicyWorkflowStepUserResponse,
  UpdateWorkflowStep,
} from 'types/procurement/workflow.model'
import { WorkflowStepModal } from './Components/WorkflowStepModal'
import { Option } from 'components/Select/Select'
import { handleErrors } from 'utils/helpers'
import { toast } from 'react-toastify'
import { AxiosError } from 'axios'

type FilterValues = {
  departmentId: string | number | null
  departmentName: string | null
}

export const WorkflowEditor = () => {
  const { values, setMultipleValues } = useFilter<FilterValues>({
    departmentId: null,
    departmentName: null,
  })

  return (
    <>
      <Alert
        title='Workflow Changes Apply to Future PRs'
        description='Shortlist candidates, compare side-by-side, then select a winner.'
        type={AlertType.WARNING}
        colorVariant={ColorVariant.PRIMARY}
      />
      <Card mt='1rem'>
        <CardBody>
          <DepartmentSelect
            label='Department'
            width='200px'
            defaultSelectFirstOption
            placeholder='Select Department'
            onChange={(value) =>
              setMultipleValues({
                ...values,
                departmentId: value?.value,
                departmentName: value?.label,
              })
            }
            value={
              values.departmentId
                ? { label: values.departmentName, value: values.departmentId }
                : null
            }
          />
        </CardBody>
      </Card>
      <Card mt='1rem'>
        <CardBody>
          {values.departmentId && <WorkflowStepsList departmentId={values.departmentId} />}
        </CardBody>
      </Card>
    </>
  )
}

const WorkflowStepsList = ({ departmentId }: { departmentId: string | number | null }) => {
  const queryClient = useQueryClient()
  const { data, isLoading } = useQuery({
    queryKey: ['procurement-workflow-steps', departmentId],
    queryFn: () => getProcurementWorkflowSteps(departmentId),
    retry: false,
  })
  const [localData, setLocalData] = useState<
    ((LocalWorkflowOptionStep | CurrentWorkflowStep) & { id: string | number })[]
  >([])
  const [stepToEdit, setStepToEdit] = useState<{
    index: number
    step: LocalWorkflowOptionStep | CurrentWorkflowStep
  } | null>(null)
  const [addStepModal, setAddStepModal] = useState(false)
  const mutation = useMutation({
    mutationFn: async () => {
      const workflowSteps: UpdateWorkflowStep[] = localData.map((step, index) => {
        const type =
          ((step.type as Option).value as WorkflowStepType) || (step.type as WorkflowStepType)
        const resolverType =
          ((step.resolverType as Option).value as WorkflowResolverType) ||
          (step.resolverType as WorkflowResolverType)
        const roleIds =
          resolverType === WorkflowResolverType.Roles
            ? step.roles?.map(
                (role: Option | ProcurementApprovalPolicyWorkflowStepRoleResponse) =>
                  (role as ProcurementApprovalPolicyWorkflowStepRoleResponse)?.role?.id ||
                  ((role as Option)?.value as number),
              )
            : undefined
        const userIds =
          resolverType === WorkflowResolverType.Users
            ? step.users?.map(
                (user: Option | ProcurementApprovalPolicyWorkflowStepUserResponse) =>
                  (user as ProcurementApprovalPolicyWorkflowStepUserResponse)?.user?.id ||
                  ((user as Option)?.value as number),
              )
            : undefined

        return {
          order: index + 1,
          name: step.name,
          type,
          resolverType,
          canEditPR: step.canEditPR,
          roleIds,
          userIds,
        }
      })

      if (workflowSteps.length === 0) {
        throw new Error('At least one workflow step is required')
      }

      const sourcingSteps = workflowSteps.filter((step) => step.type === WorkflowStepType.Sourcing)

      if (sourcingSteps.length > 1) {
        throw new Error('You must have one sourcing step')
      }

      if (sourcingSteps.length === 0) {
        throw new Error('You must have one sourcing step')
      }

      // Sourcing step and steps after sourcing must have canEditPR set to false validator
      if (sourcingSteps.length > 0) {
        const sourcingStep = sourcingSteps[0]
        if (sourcingStep.canEditPR) {
          throw new Error('Sourcing cannot have "Can Edit PR" enabled')
        }
        workflowSteps.slice(sourcingStep.order).forEach((step) => {
          if (step.canEditPR) {
            throw new Error('Steps after sourcing cannot have "Can Edit PR" enabled')
          }
        })
      }

      if (data) {
        await updateProcurementWorkflowSteps(data.id, workflowSteps)
      } else {
        await createProcurementWorkflowSteps({
          departmentId: Number(departmentId),
          workflowSteps,
        })
      }
    },
    onSuccess: () => {
      toast.success('Workflow steps updated successfully')
      queryClient.invalidateQueries({ queryKey: ['procurement-workflow-steps', departmentId] })
    },
    onError: (error) => {
      const errorMessage = error.message

      if (errorMessage) {
        toast.error(errorMessage)
        return
      }
      handleErrors(error)
    },
  })

  const addStepToLocalData = (step: LocalWorkflowOptionStep) => {
    const id = Math.random().toString(36).substring(2, 15)
    setLocalData([...localData, { ...step, id }])
  }
  const updateStepInLocalData = (step: LocalWorkflowOptionStep, index: number | undefined) => {
    const id = Math.random().toString(36).substring(2, 15)
    setLocalData(localData.map((s, i) => (i === index ? { ...step, id } : s)))
  }
  const deleteStepFromLocalData = (index: number) => {
    setLocalData(localData.filter((_, i) => i !== index))
  }

  useEffect(() => {
    setLocalData(data?.workflowSteps || [])
  }, [data])

  const edited = useMemo(() => {
    return JSON.stringify(localData) !== JSON.stringify(data?.workflowSteps)
  }, [localData, data])

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (over && active) {
      const activeIndex = localData.findIndex((step) => step.id === active.id)
      const overIndex = localData.findIndex((step) => step.id === over.id)
      setLocalData(arrayMove(localData, activeIndex, overIndex))
    }
  }

  return (
    <>
      <Spinner loading={mutation.isPending} />
      <Flex between center>
        <NormalText>Workflow Steps</NormalText>
        <Button orange onClick={() => setAddStepModal(true)}>
          <Flex gap='0.5rem' center>
            <Icon size='16px'>{PlusIcon}</Icon>
            Add Step
          </Flex>
        </Button>
      </Flex>
      <Flex gap='1rem' column mt='16'>
        {isLoading ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '400px',
            }}
          >
            <JustSpinner />
          </div>
        ) : localData?.length > 0 ? (
          <DndContext onDragEnd={handleDragEnd}>
            <SortableContext
              items={localData.map((step, index) => step.id)}
              strategy={verticalListSortingStrategy}
            >
              {localData.map((step, index) => (
                <WorkflowSteps
                  overlay={false}
                  key={step.name + index}
                  step={step}
                  index={index}
                  removeStep={() => deleteStepFromLocalData(index)}
                  onEdit={() =>
                    setStepToEdit({
                      index,
                      step,
                    })
                  }
                />
              ))}
            </SortableContext>
          </DndContext>
        ) : (
          <EmptyList
            title='No workflow steps found'
            description='No workflow steps found for this department'
          />
        )}
      </Flex>
      {edited && (
        <StyckyFooter>
          <Flex gap='1rem'>
            <Button onClick={() => setLocalData(data?.workflowSteps || [])}>Reset</Button>
            <Button orange onClick={() => mutation.mutate()}>
              Save
            </Button>
          </Flex>
        </StyckyFooter>
      )}

      {addStepModal && (
        <WorkflowStepModal
          closeModal={() => setAddStepModal(false)}
          dataFunction={(data) => {
            addStepToLocalData(data)
          }}
        />
      )}
      {stepToEdit && (
        <WorkflowStepModal
          closeModal={() => setStepToEdit(null)}
          dataFunction={(data) => {
            updateStepInLocalData(data, stepToEdit.index)
          }}
          step={
            'order' in stepToEdit.step
              ? {
                  name: stepToEdit.step.name,
                  type: {
                    label:
                      stepToEdit.step.type === WorkflowStepType.Approval ? 'Approval' : 'Sourcing',
                    value:
                      stepToEdit.step.type === WorkflowStepType.Approval
                        ? WorkflowStepType.Approval
                        : WorkflowStepType.Sourcing,
                  },
                  resolverType: {
                    label:
                      stepToEdit.step.resolverType === WorkflowResolverType.Roles
                        ? 'Roles'
                        : 'Users',
                    value:
                      stepToEdit.step.resolverType === WorkflowResolverType.Roles
                        ? WorkflowResolverType.Roles
                        : WorkflowResolverType.Users,
                  },
                  roles: stepToEdit.step.roles?.map((role) => ({
                    label: role.role?.name,
                    value: role.role?.id,
                  })),
                  users: stepToEdit.step.users?.map((user) => ({
                    label: user.user?.firstName + ' ' + user.user?.lastName,
                    value: user.user?.id,
                  })),
                  canEditPR: stepToEdit.step.canEditPR,
                }
              : stepToEdit.step
          }
        />
      )}
    </>
  )
}

const WorkflowSteps = ({
  step,
  index,
  onEdit,
  overlay,
  removeStep,
}: {
  step: (CurrentWorkflowStep | LocalWorkflowOptionStep) & { id: string | number }
  index: number
  onEdit: () => void
  overlay: boolean
  removeStep: () => void
}) => {
  const roles = step.roles?.map((role) => role.label || role.role?.name).join(', ')
  const users = step.users
    ?.map((user) => user.label || user.user?.firstName + ' ' + user.user?.lastName)
    .join(', ')

  const stepType =
    step.type === WorkflowStepType.Approval ||
    (step.type as Option)?.value === WorkflowStepType.Approval
      ? WorkflowStepType.Approval
      : WorkflowStepType.Sourcing

  const stepResolverType =
    step.resolverType === WorkflowResolverType.Roles ||
    (step.resolverType as Option)?.value === WorkflowResolverType.Roles
      ? WorkflowResolverType.Roles
      : WorkflowResolverType.Users

  const { setNodeRef, listeners, attributes, transform, transition, isDragging } = useSortable({
    id: step.id,
  })

  const styles: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition,
    zIndex: overlay ? 1000 : 0,
    opacity: isDragging ? 0.3 : 1,
  }

  return (
    <Card ref={setNodeRef} {...attributes} style={styles}>
      <CardBody>
        <Flex gap='1rem' width='100%'>
          <div {...listeners} style={{ cursor: isDragging ? 'grabbing' : 'grab' }}>
            <ReorganizeIcon />
          </div>
          <Circle>{index + 1}</Circle>
          <Flex column width='100%' gap='0.75rem'>
            {/* TITLE */}
            <Flex between width='100%'>
              <Flex column gap='0.25rem'>
                <NormalText semibold>{step.name}</NormalText>
                <MediumText colorVariant={ColorVariant.SECONDARY}>
                  {stepType === WorkflowStepType.Approval ? 'Approval step' : 'Sourcing step'}
                </MediumText>
              </Flex>
              <CloseIcon onClick={removeStep} />
            </Flex>
            {/* DESCRIPTION */}
            <Grid>
              <Flex column gap='0.25rem'>
                <MediumText colorVariant={ColorVariant.SECONDARY}>APPROVERS</MediumText>
                <MediumText>
                  {stepResolverType === WorkflowResolverType.Roles ? roles : users}
                </MediumText>
              </Flex>
              <Flex column gap='0.25rem'>
                <MediumText colorVariant={ColorVariant.SECONDARY}>CAN REQUEST EDIT</MediumText>
                <MediumText>{step.canEditPR ? 'Yes' : 'No'}</MediumText>
              </Flex>
            </Grid>
            <Divider />
            <MediumText
              colorVariant={ColorVariant.PRIMARY}
              bold
              pointer
              hoverUnderline
              onClick={onEdit}
            >
              Edit Step Configuration
            </MediumText>
          </Flex>
        </Flex>
      </CardBody>
    </Card>
  )
}

const Circle = styled.div`
  min-width: 2rem;
  min-height: 2rem;
  width: 2rem;
  height: 2rem;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${(props) => props.theme.colorOrange};
  color: ${(props) => props.theme.dividerColor};
  font-weight: 700;
`

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 350px));
  gap: 1rem;
`

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${(props) => props.theme.dividerColor};
`
